// Elintra — Stores Page | v3.2 | Unchanged from v3.1

import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle2, Database, ExternalLink, Plus, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorBanner } from '@/components/shared/ErrorBanner';
import { SkeletonCard } from '@/components/shared/SkeletonCard';
import { formatLastSync } from '@/lib/utils';
import type { StoreRecord, StoreStatus } from '@/types/app';
import type { BadgeVariant } from '@/types/components.types';

const STATUS_CONFIG: Record<StoreStatus, {
  variant: BadgeVariant; label: string; icon: React.ElementType; dotColor: string;
}> = {
  connected: { variant: 'success', label: 'Connected', icon: CheckCircle2, dotColor: 'bg-emerald-400' },
  syncing:   { variant: 'warning', label: 'Syncing',   icon: RefreshCw,    dotColor: 'bg-amber-400'  },
  attention: { variant: 'danger',  label: 'Attention', icon: AlertCircle,  dotColor: 'bg-red-400'    },
};

const PLATFORM_COLORS: Record<string, string> = {
  Stripe:       'text-violet-400',
  Shopify:      'text-emerald-400',
  Gumroad:      'text-pink-400',
  LemonSqueezy: 'text-yellow-400',
};

export const StoresPage = ({
  stores, isLoading, error,
}: {
  stores:    StoreRecord[];
  isLoading: boolean;
  error:     string | null;
}) => {
  const connected = stores.filter((s) => s.status === 'connected').length;
  const total     = stores.length;

  if (isLoading && stores.length === 0) {
    return (
      <div className="max-w-4xl space-y-4">
        <SkeletonCard rows={2} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SkeletonCard rows={4} />
          <SkeletonCard rows={4} />
        </div>
      </div>
    );
  }

  // Live backend returns no stores yet (Phase-2C) → clean empty state, not fixtures.
  if (!isLoading && stores.length === 0 && !error) {
    return (
      <div className="max-w-4xl space-y-6">
        <Card className="p-10 flex flex-col items-center justify-center text-center gap-4">
          <div className="w-14 h-14 rounded-full bg-violet-500/10 border border-violet-500/20
                          flex items-center justify-center">
            <Database className="w-6 h-6 text-violet-400" />
          </div>
          <div>
            <p className="font-semibold text-base">No stores connected yet</p>
            <p className="text-sm text-zinc-400 mt-1 max-w-md">
              Storefront onboarding ships in Phase&nbsp;2C. Once connected, your Stripe,
              Shopify, Gumroad, or LemonSqueezy catalog appears here with live sync and AEO scores.
            </p>
          </div>
          <Badge variant="info" className="text-xs">Phase 2C</Badge>
          <Button variant="primary" size="sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" />Connect a store
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      <AnimatePresence>
        {error && <ErrorBanner message={error} />}
      </AnimatePresence>

      {/* Summary Banner */}
      <Card className="p-5 flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-violet-500/10 flex items-center justify-center shrink-0">
          <Database className="w-5 h-5 text-violet-400" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-sm">{connected} of {total} storefronts connected</p>
          <p className="text-xs text-zinc-400 mt-0.5">
            {stores.reduce((sum, s) => sum + s.productCount, 0).toLocaleString()} products synced across all stores
          </p>
        </div>
        <Button variant="primary" size="sm">
          <Plus className="w-3.5 h-3.5 mr-1.5" />Add Store
        </Button>
      </Card>

      {/* Store Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {stores.map((store, i) => {
          const cfg         = STATUS_CONFIG[store.status];
          const platformClr = PLATFORM_COLORS[store.platform] ?? 'text-zinc-400';
          return (
            <motion.div key={store.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}>
              <Card className="p-5 h-full flex flex-col gap-4">
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-zinc-800 border border-zinc-700
                                    flex items-center justify-center shrink-0">
                      <Database className={`w-4 h-4 ${platformClr}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">{store.name}</p>
                      <p className={`text-xs font-medium ${platformClr}`}>{store.platform}</p>
                    </div>
                  </div>
                  <Badge variant={cfg.variant} className="gap-1.5 shrink-0">
                    <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotColor}`} />
                    {cfg.label}
                  </Badge>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-zinc-900/60 rounded-lg p-3 border border-zinc-800/60">
                    <p className="text-lg font-bold font-mono">{store.productCount}</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">Products</p>
                  </div>
                  <div className="bg-zinc-900/60 rounded-lg p-3 border border-zinc-800/60">
                    <p className="text-lg font-bold font-mono text-emerald-400">
                      {store.complianceScore ?? '—'}
                      {store.complianceScore ? '%' : ''}
                    </p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">AEO Score</p>
                  </div>
                  <div className="bg-zinc-900/60 rounded-lg p-3 border border-zinc-800/60">
                    <p className="text-xs font-mono text-zinc-300 mt-0.5">
                      {formatLastSync(store.lastSync)}
                    </p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">Last Sync</p>
                  </div>
                </div>

                {/* Footer actions */}
                <div className="flex items-center gap-2 mt-auto pt-1">
                  <Button variant="outline" size="sm" className="flex-1">
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Sync Now
                  </Button>
                  <Button variant="ghost" size="sm">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </div>

                {/* Attention state warning */}
                {store.status === 'attention' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-start gap-2 px-3 py-2 rounded-lg
                               bg-red-500/8 border border-red-500/20 text-xs text-red-400">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>Store requires attention — re-authenticate or check webhook settings.</span>
                  </motion.div>
                )}
              </Card>
            </motion.div>
          );
        })}

        {/* Add New Store CTA Card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: stores.length * 0.07 }}>
          <Card className="p-5 h-full flex flex-col items-center justify-center gap-4
                           border-dashed cursor-pointer hover:border-zinc-600
                           hover:bg-zinc-800/20 transition-all min-h-[200px]">
            <div className="w-12 h-12 rounded-full bg-zinc-800/60 flex items-center justify-center">
              <Plus className="w-6 h-6 text-zinc-500" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-sm text-zinc-400">Connect new store</p>
              <p className="text-xs text-zinc-600 mt-1">Stripe, Shopify, Gumroad, or LemonSqueezy</p>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
